import Button from '@carvana/ds-button';
import List from '@carvana/ds-list';
import cx from 'classnames';
import { Check } from 'lucide-react';
import React, { useState } from 'react';
import { EventedModal, ModalNames } from '|/app/modals';
import { StepName } from '|/app/services';
import { useBrand, useBrandCopy } from '|/brand';
import WsysModal from '|/components/carvana-ds/wsys-modal';
import WsysPromoCard from '|/components/carvana-ds/wsys-promo-card';
import currency from '|/helpers/currency';
import { getStateNameFromAbbreviation } from '|/helpers/us-states';
import useNavigateToStep from '|/hooks/use-navigate-to-step';
import { ReviewSummaryResponse } from '|/services/purchase-api';
import styles from './trade-in-details-modal.module.css';
import { deriveTradeInDetails } from '../../derive-trade-in-details';
import panel from '../../price-details.module.css';
import { AmountRow } from '../amount-row';

/**
 * ds-modal derives its z-index from nesting depth (1300 + depth * 100). The savings modal
 * renders as a *sibling* of this one rather than inside it, so it reads the same depth as its
 * opener and would tie. 1600 clears this modal in both of its hosts — depth 1 behind the
 * Price Details sheet, depth 0 on review order.
 */
const SAVINGS_MODAL_Z_INDEX = 1600;

interface TradeInDetailsModalProps {
  summary: ReviewSummaryResponse;
  isOpen: boolean;
  onClose: () => void;
}

const TradeInDetailsModal = ({ summary, isOpen, onClose }: TradeInDetailsModalProps) => {
  const [isSavingsModalOpen, setIsSavingsModalOpen] = useState(false);
  const navigateToStep = useNavigateToStep();
  const { identity } = useBrand();
  const copy = useBrandCopy();

  const view = deriveTradeInDetails({ summary, tradeBrand: identity.financeBrand });
  const stateName =
    getStateNameFromAbbreviation(summary?.customer?.address?.state ?? '') ?? 'your state';

  return (
    <>
      <EventedModal
        analyticsId={ModalNames.TradeIn}
        aria-label="Trade-in details"
        isOpen={isOpen}
        onClose={onClose}
        mobileStyle="inset"
        desktopWidth="480px"
        divLine={false}
        contentPadding={false}
        disableOutsideClose={isSavingsModalOpen}
      >
        <div className={styles.content} data-testid="pp_trade-in-details-modal">
          <div className={styles.vehicleRow}>
            {view.imageUrl && (
              <img src={view.imageUrl} alt={view.vehicleTitle} className={styles.vehicleImage} />
            )}
            <div className={styles.vehicleInfo}>
              <p className={styles.vehicleTitle}>
                {view.vehicleYearMake && (
                  <span className={styles.vehicleTitleLine}>{view.vehicleYearMake}</span>
                )}
                {view.vehicleModelTrim && (
                  <span className={styles.vehicleTitleLine}>{view.vehicleModelTrim}</span>
                )}
              </p>
              {view.mileage && <p className={styles.vehicleMileage}>{view.mileage}</p>}
            </div>
          </div>

          <div className={styles.body}>
            <div className={styles.offer}>
              <p className={styles.offerLabel}>{copy.tradeOfferLabel}</p>
              <div className={styles.offerAmountRow}>
                <p className={styles.offerAmount} data-testid="pp_trade-in-details_offer">
                  {currency(view.offerAmount, {
                    minimumFractionDigits: 0,
                    maximumFractionDigits: 0
                  })}
                </p>
                {view.expiresOn && (
                  <p className={styles.offerExpiry} data-testid="pp_trade-in-details_expiry">
                    Expires
                    <br />
                    {view.expiresOn}
                  </p>
                )}
              </div>
            </div>

            {view.showSavingsBanner && (
              // The card forwards `analyticsId` to its own link, which the rule can't see
              // through — same disable as the trade-in step's call site.
              // eslint-disable-next-line local-rules/on-click-analytics-id
              <WsysPromoCard
                className={styles.savingsBanner}
                layout="wide"
                tradeIncentive={view.incentive}
                taxSavings={view.taxSavings}
                analyticsId="pp_trade_in_modal_savings_details"
                onClick={() => {
                  setIsSavingsModalOpen(true);
                }}
              />
            )}

            <div className={styles.benefitsGroup}>
              <div className={styles.benefits}>
                <p className={styles.benefitsTitle}>Benefits of including this trade-in</p>
                <div className={styles.benefitList}>
                  {view.benefits.map(benefit => (
                    <div key={benefit.id} className={styles.benefit}>
                      <List
                        variant="icon"
                        size="small"
                        icon={<Check size={16} color="var(--icon-primary)" />}
                        items={[
                          {
                            key: benefit.id,
                            content: <span className={styles.benefitTitle}>{benefit.title}</span>
                          }
                        ]}
                      />
                      <p className={styles.benefitBody}>{benefit.body}</p>
                    </div>
                  ))}
                </div>
              </div>

              {view.showPayoff && (
                <div className={panel.payoffPanel} data-testid="pp_trade-in-details_payoff">
                  <p className={panel.payoffTitle}>Paying off your existing loan</p>
                  <div className={panel.payoffRows}>
                    {view.payoffRows.map((row, index) => (
                      // The divider always sits on the row above the net result, which is the
                      // offer row under negative equity and the balance row otherwise.
                      <div
                        key={row.id}
                        className={cx({
                          [panel.payoffBalance]: index === view.payoffRows.length - 2
                        })}
                      >
                        <AmountRow
                          label={row.label}
                          amount={`${row.amount < 0 ? '-' : ''}${currency(Math.abs(row.amount))}`}
                          testId={`pp_trade-in-details_payoff-${row.id}`}
                        />
                        {row.note && <p className={panel.payoffNote}>{row.note}</p>}
                      </div>
                    ))}
                  </div>
                  <p className={panel.payoffMessage}>{view.payoffBody}</p>
                </div>
              )}
            </div>
          </div>

          <div className={styles.actions}>
            <Button
              variant="tertiary"
              onClick={() => {
                navigateToStep(StepName.TradeIn);
              }}
              data-analytics-id="pp_trade_in_modal_edit_button"
              data-testid="pp_trade-in-details_edit"
            >
              Edit
            </Button>
            <Button
              onClick={onClose}
              data-analytics-id="pp_trade_in_modal_close_button"
              data-testid="pp_trade-in-details_close"
            >
              Close
            </Button>
          </div>
        </div>
      </EventedModal>

      {/* Outside the modal above: ds-modal renders in place rather than through a portal, and
          animates its container with a transform, which would make this modal's fixed
          positioning resolve against that container instead of the viewport. */}
      <WsysModal
        isOpen={isSavingsModalOpen}
        onClose={() => {
          setIsSavingsModalOpen(false);
        }}
        zIndex={SAVINGS_MODAL_Z_INDEX}
        tradeIncentive={view.incentive}
        taxSavings={view.taxSavings}
        vehicleMake={summary?.tradeIn?.make ?? ''}
        tradeValue={currency(view.offerAmount, {
          minimumFractionDigits: 0,
          maximumFractionDigits: 0
        })}
        stateName={stateName}
      />
    </>
  );
};

export default TradeInDetailsModal;
